import { apiSlice } from "../../api/apiSlice";
// const baseString = "/api/books";

export const TASK_VERIFICATION_PAGE_SIZE = 12;

const buildTaskVerificationQueryParams = ({
  type,
  selectedCompany,
  page = 1,
  limit = TASK_VERIFICATION_PAGE_SIZE,
  search,
  sortBy,
  sortOrder,
}) => {
  const params = new URLSearchParams();
  if (type) params.set("type", type);
  if (selectedCompany) params.set("selectedCompany", String(selectedCompany));
  if (search) params.set("search", search);
  if (sortBy) params.set("sortBy", sortBy);
  if (sortOrder) params.set("sortOrder", sortOrder);
  params.set("page", String(page));
  params.set("limit", String(limit));
  return params;
};

const serializeTaskVerificationQueryArgs = (queryArgs) =>
  [
    queryArgs?.type ?? "",
    queryArgs?.selectedCompany ?? "",
    queryArgs?.search ?? "",
    queryArgs?.sortBy ?? "",
    queryArgs?.sortOrder ?? "",
  ].join("-");

const mergeTaskVerificationPages = (currentCache, newItems, { arg }) => {
  if (!newItems?.data) {
    return newItems;
  }

  if (arg.page === 1) {
    return newItems;
  }

  return {
    ...newItems,
    data: {
      ...newItems.data,
      taskCompletionRequests: [
        ...(currentCache?.data?.taskCompletionRequests ?? []),
        ...(newItems.data?.taskCompletionRequests ?? []),
      ],
      pagination: newItems.data.pagination,
    },
  };
};

export const booksApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // @desc: Get all books according to query

    // BOOKS ROUTES
    getBookThemes: builder.query({
      query: () => `/api/books/bookTheme`,
      providesTags: ["Books"],
    }),
    getBooks: builder.query({
      query: (arg) => {
        // Support both old format (string) and new format (object)
        const type = typeof arg === 'string' ? arg : arg?.type;
        const selectedCompany = typeof arg === 'object' ? arg?.selectedCompany : null;

        const isCompanyType = type?.toUpperCase() === "COMPANY";
        let url = `/api/books?type=${type}`;
        if (isCompanyType) {
          url += `&selectedCompany=${selectedCompany}`;
        } else if (selectedCompany) {
          url += `&selectedCompany=${selectedCompany}`;
        }
        return url;
      },
      providesTags: ["Books"],
    }),
    getBookById: builder.query({
      query: (_id) => ({
        url: `/api/books/${_id}`,
        method: "GET",
      }),
      providesTags: ["BookById"],
    }),
    updateBook: builder.mutation({
      query: ({ _id, data }) => ({
        url: `/api/books/${_id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Books", "BookById"],
    }),
    publishBook: builder.mutation({
      query: ({ _id, publishBookBody }) => {
        const formData = new FormData();
        formData.append("badgeName", publishBookBody?.badgeName);
        formData.append("image", publishBookBody?.image);
        return {
          url: `/api/books/publish/${_id}`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: ["Books", "BookById"],
    }),
    unPublishBook: builder.mutation({
      query: ({ _id }) => {
        return {
          url: `/api/books/unpublish/${_id}`,
          method: "PATCH",
        };
      },
      invalidatesTags: ["Books", "BookById"],
    }),
    createBook: builder.mutation({
      query: (createBookBody) => {
        return {
          url: "/api/books",
          method: "POST",
          body: createBookBody,
        }
      },
      invalidatesTags: ["Books"],
    }),
    deleteBook: builder.mutation({
      query: (bookId) => ({
        url: `/api/books/${bookId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Books"],
    }),

    // LEVELS ROUTES
    getLevelsById: builder.query({
      query: (_id) => ({
        url: `/api/books/level/${_id}`,
        method: "GET",
      }),
      providesTags: ["EmployeeTask"],
    }),
    getBooksLevels: builder.query({
      query: (bookId) => ({
        url: `/api/books/${bookId}/level`,
        method: "GET",
      }),
      providesTags: ["BooksLevels"],
    }),
    createLevel: builder.mutation({
      query: ({ bookId, level }) => ({
        url: `/api/books/${bookId}/level`,
        method: "POST",
        body: level,
      }),
      invalidatesTags: ["BooksLevels"],
    }),
    updateLevel: builder.mutation({
      query: ({ levelId, updateLevelBody }) => ({
        url: `/api/books/level/${levelId}`,
        method: "PATCH",
        body: updateLevelBody,
      }),
      invalidatesTags: ["BooksLevels"],
    }),
    deleteLevel: builder.mutation({
      query: (_id) => ({
        url: `/api/books/level/${_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BooksLevels", "BookById"],
    }),

    // TASKS ROUTES
    getLevelTasks: builder.query({
      query: (levelId) => ({
        url: `/api/books/level/${levelId}/task`,
        method: "GET",
      }),
      providesTags: ["BooksLevelsTask"],
    }),
    getTaskById: builder.query({
      query: (taskId) => ({
        url: `/api/books/task/${taskId}`,
        method: "GET",
      }),
    }),
    createTask: builder.mutation({
      query: ({ bookId, levelId, task }) => ({
        url: `/api/books/${bookId}/level/${levelId}/task`,
        method: "POST",
        body: {
          name: task.name,
          description: task.description,
          course: task.course,
          points: task.points,
        },
      }),
      invalidatesTags: ["BooksLevelsTask", "BookById"],
    }),
    updateTask: builder.mutation({
      query: ({ taskId, updateTaskBody }) => ({
        url: `/api/books/task/${taskId}`,
        method: "PATCH",
        body: { ...updateTaskBody },
      }),
      invalidatesTags: ["BooksLevelsTask"],
    }),
    deleteTask: builder.mutation({
      query: (_id) => ({
        url: `/api/books/task/${_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BooksLevelsTask", "BookById"],
    }),
    getTaskCourse: builder.query({
      query: (companyId) => ({
        url: `api/books/get/task-courses?selectedCompany=${companyId}`,
        method: "GET",
      }),
    }),

    //COMPANY ADMIN TASK
    getPendingCompanyAdminTask: builder.query({
      query: ({
        type,
        page = 1,
        limit = TASK_VERIFICATION_PAGE_SIZE,
        search,
        sortBy,
        sortOrder,
      }) => ({
        url: `/api/books/task/pending/companyAdmin?${buildTaskVerificationQueryParams({
          type,
          page,
          limit,
          search,
          sortBy,
          sortOrder,
        }).toString()}`,
        method: "GET",
      }),
      serializeQueryArgs: ({ queryArgs }) =>
        serializeTaskVerificationQueryArgs(queryArgs),
      merge: mergeTaskVerificationPages,
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg.page !== previousArg?.page,
    }),

    // Task Employee view

    sendTask: builder.mutation({
      query: ({ bookId, levelId, taskId, taskData }) => ({
        url: `/api/books/${bookId}/level/${levelId}/task/${taskId}/complete`,
        method: "POST",
        body: taskData,
      }),
      invalidatesTags: ["UserActivity"],
    }),

    getPendingTask: builder.query({
      query: ({
        type,
        selectedCompany,
        page = 1,
        limit = TASK_VERIFICATION_PAGE_SIZE,
        search,
        sortBy,
        sortOrder,
      }) => ({
        url: `/api/books/task/pending?${buildTaskVerificationQueryParams({
          type,
          selectedCompany,
          page,
          limit,
          search,
          sortBy,
          sortOrder,
        }).toString()}`,
        method: "GET",
      }),
      serializeQueryArgs: ({ queryArgs }) =>
        serializeTaskVerificationQueryArgs(queryArgs),
      merge: mergeTaskVerificationPages,
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg.page !== previousArg?.page,
    }),

    getPendingTaskById: builder.query({
      query: (arg) => {
        const _id = typeof arg === "string" ? arg : arg?._id;
        const status = typeof arg === "object" ? arg?.status : null;
        const type = typeof arg === "object" ? arg?.type : null;
        const page = typeof arg === "object" ? arg?.page : null;
        const limit = typeof arg === "object" ? arg?.limit : null;
        const bookId = typeof arg === "object" ? arg?.bookId : null;
        const params = new URLSearchParams();
        if (status) params.set("status", status);
        if (type) params.set("type", type);
        if (page) params.set("page", page);
        if (limit) params.set("limit", limit);
        if (bookId) params.set("bookId", bookId);
        const queryParams = params.toString() ? `?${params.toString()}` : "";
        return {
          url: `/api/books/task/pending/${_id}${queryParams}`,
          method: "GET",
        };
      },
      providesTags: ["Tasks"],
    }),
    updateTaskStatus: builder.mutation({
      query: ({ status, taskCompletionId, rejectionReason }) => ({
        url: `/api/books/task/complete/${taskCompletionId}/${status}`,
        method: "PATCH",
        ...(status === "reject"
          ? { body: { rejectionReason: rejectionReason ?? "" } }
          : {}),
      }),
      invalidatesTags: ["Tasks", "EmployeeTask", "Points", "UserActivity"],
    }),

    // ADMIN ROUTES
    getPendingTaskCompletion: builder.query({
      query: () => ({
        url: `/api/books/task/pending`,
        method: "GET",
      }),
      providesTags: ["TaskCompletion"],
    }),
    getApproveTaskCompletion: builder.mutation({
      query: (taskCompletionId) => ({
        url: `/api/books/task/complete/${taskCompletionId}/approve`,
        method: "GET",
      }),
      invalidatesTags: ["TaskCompletion", "Points", "UserActivity"],
    }),
    getRejectTaskCompletion: builder.mutation({
      query: (taskCompletionId) => ({
        url: `/api/books/task/complete/${taskCompletionId}/reject`,
        method: "GET",
      }),
      invalidatesTags: ["TaskCompletion", "UserActivity"],
    }),

    createTaskCompletion: builder.mutation({
      query: ({ bookId, levelId, taskId, completionData }) => ({
        url: `/api/books/${bookId}/level/${levelId}/task/${taskId}/complete`,
        method: "POST",
        body: completionData,
      }),
      invalidatesTags: ["BooksLevelsTask", "UserActivity"],
    }),
    resumeTask: builder.query({
      query: (userId) => ({
        url: `/api/user/ongoing-book/${userId}`,
        method: "GET",
      }),
    }),
    rateBook: builder.mutation({
      query: ({ bookId, rating }) => ({
        url: `/api/books/rate/b/${bookId}`,
        method: "POST",
        body: { rating },
      }),
    }),
    getBookRating: builder.query({
      query: ({ bookId }) => ({
        url: `/api/books/rate/b/${bookId}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  // Book hooks
  useGetBookThemesQuery,
  useGetBooksQuery,
  useGetBookByIdQuery,
  useUpdateBookMutation,
  usePublishBookMutation,
  useUnPublishBookMutation,
  useCreateBookMutation,
  useDeleteBookMutation,

  // Level hooks
  useGetLevelsByIdQuery,
  useGetBooksLevelsQuery,
  useGetLevelTasksQuery,
  useCreateLevelMutation,
  useUpdateLevelMutation,
  useDeleteLevelMutation,

  // Task hooks
  useGetTaskByIdQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useGetTaskCourseQuery,

  //COMPANY ADMIN TASK
  useGetPendingCompanyAdminTaskQuery,

  // Employee view tasks
  useSendTaskMutation,
  useGetPendingTaskQuery,
  useGetPendingTaskByIdQuery,
  useUpdateTaskStatusMutation,

  // Resume Task
  useResumeTaskQuery,

  // Admin hooks
  useGetPendingTaskCompletionQuery,
  useGetBookRatingQuery,
  useGetApproveTaskCompletionMutation,
  useGetRejectTaskCompletionMutation,
  useCreateTaskCompletionMutation,
  useRateBookMutation,
} = booksApiSlice;
